import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDialogAccessibility from "../../hooks/useDialogAccessibility";

const TASK_COLUMNS = [
  { status: "todo", titleKey: "tasks.planned" },
  { status: "inProgress", titleKey: "tasks.inProgress" },
  { status: "done", titleKey: "tasks.done" },
];

const EMPTY_TASK = {
  id: "",
  name: "",
  description: "",
  status: "todo",
};

const normalizeStatus = (status) =>
  TASK_COLUMNS.some((column) => column.status === status) ? status : "todo";

const createTaskId = () =>
  window.crypto?.randomUUID?.() ??
  `task-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const TaskBoard = ({ tasks = [], onChange }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(null);
  const editorRef = useRef(null);
  const titleInputRef = useRef(null);
  const editorTitleId = useId();

  useDialogAccessibility({
    containerRef: editorRef,
    initialFocusRef: titleInputRef,
    isOpen: Boolean(draft),
    onClose: () => setDraft(null),
  });

  const openNewTask = (status) => {
    setDraft({ ...EMPTY_TASK, status });
  };

  const openTask = (task) => {
    setDraft({ ...task, status: normalizeStatus(task.status) });
  };

  const saveTask = (event) => {
    event.preventDefault();
    const normalizedTask = {
      ...draft,
      id: draft.id || createTaskId(),
      name: draft.name.trim(),
      description: draft.description.trim(),
      status: normalizeStatus(draft.status),
    };

    if (!normalizedTask.name) return;

    const exists = tasks.some((task) => task.id === normalizedTask.id);
    onChange(
      exists
        ? tasks.map((task) =>
            task.id === normalizedTask.id ? normalizedTask : task
          )
        : [...tasks, normalizedTask]
    );
    setDraft(null);
  };

  const deleteTask = () => {
    if (!draft?.id || !window.confirm(t("tasks.deleteConfirm"))) return;
    onChange(tasks.filter((task) => task.id !== draft.id));
    setDraft(null);
  };

  const moveTask = (taskId, status) => {
    onChange(
      tasks.map((task) =>
        task.id === taskId ? { ...task, status: normalizeStatus(status) } : task
      )
    );
  };

  return (
    <section className="journal-section task-board-section">
      <div className="journal-section-heading">
        <div>
          <h2>{t("tasks.title")}</h2>
          <p>{t("tasks.hint")}</p>
        </div>
      </div>

      <div className="task-board">
        {TASK_COLUMNS.map((column) => {
          const columnTasks = tasks.filter(
            (task) => normalizeStatus(task.status) === column.status
          );

          return (
            <section
              className={`task-column task-column--${column.status}`}
              key={column.status}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                moveTask(event.dataTransfer.getData("text/task-id"), column.status);
              }}
            >
              <header>
                <h3>{t(column.titleKey)}</h3>
                <span>{columnTasks.length}</span>
                <button
                  aria-label={t("tasks.addTo", { column: t(column.titleKey) })}
                  type="button"
                  onClick={() => openNewTask(column.status)}
                >
                  +
                </button>
              </header>

              <div className="task-column-notes">
                {columnTasks.length === 0 && (
                  <p className="task-column-empty">{t("tasks.empty")}</p>
                )}
                {columnTasks.map((task) => (
                  <article
                    className="task-note"
                    draggable
                    key={task.id}
                    onDragStart={(event) => {
                      event.dataTransfer.setData("text/task-id", task.id);
                      event.dataTransfer.effectAllowed = "move";
                    }}
                    onClick={() => openTask(task)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openTask(task);
                      }
                    }}
                    role="button"
                    tabIndex="0"
                  >
                    <div>
                      <h4>{task.name}</h4>
                      {task.description && <p>{task.description}</p>}
                    </div>
                    <button
                      aria-label={t("tasks.edit", { name: task.name })}
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openTask(task);
                      }}
                    >
                      ⋯
                    </button>
                  </article>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {draft && (
        <div className="task-editor-overlay" onMouseDown={() => setDraft(null)}>
          <form
            aria-labelledby={editorTitleId}
            aria-modal="true"
            className="task-editor"
            ref={editorRef}
            role="dialog"
            tabIndex="-1"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={saveTask}
          >
            <header>
              <h3 id={editorTitleId}>{draft.id ? t("tasks.editNote") : t("tasks.newNote")}</h3>
              <button
                aria-label={t("common.close")}
                type="button"
                onClick={() => setDraft(null)}
              >
                ×
              </button>
            </header>

            <label>
              {t("tasks.titleField")}
              <input
                ref={titleInputRef}
                required
                value={draft.name}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              {t("tasks.descriptionField")}
              <textarea
                rows="6"
                value={draft.description}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              {t("tasks.statusField")}
              <select
                value={draft.status}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    status: event.target.value,
                  }))
                }
              >
                {TASK_COLUMNS.map((column) => (
                  <option key={column.status} value={column.status}>
                    {t(column.titleKey)}
                  </option>
                ))}
              </select>
            </label>

            <footer>
              {draft.id && (
                <button
                  className="task-delete-button"
                  type="button"
                  onClick={deleteTask}
                >
                  {t("tasks.delete")}
                </button>
              )}
              <button type="button" onClick={() => setDraft(null)}>
                {t("common.cancel")}
              </button>
              <button className="task-save-button" type="submit">
                {t("tasks.save")}
              </button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
};

export default TaskBoard;
